import * as React from 'react';

export const useDebouncedEffect = (
  fnc: () => void,
  delay: number,
  deps: unknown[],
) => {
  const ref = React.useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  const fnRef = React.useRef(fnc);

  fnRef.current = fnc;

  React.useEffect(() => {
    clearTimeout(ref.current);
    ref.current = setTimeout(() => {
      fnRef.current();
      clearTimeout(ref.current);
    }, delay);
    return () => clearTimeout(ref.current);
  }, [...deps, delay]);
};

export const useDebouncedValue = <T>(
  value: T,
  delay: number,
  { maxWait }: { maxWait?: number } = {},
) => {
  const [debouncedValue, setDebouncedValue] = React.useState<T>(value);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxWaitTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const lastCallTimeRef = React.useRef<number>(Date.now());

  React.useEffect(() => {
    const now = Date.now();

    const updateValue = () => {
      setDebouncedValue(value);
      lastCallTimeRef.current = now;
    };

    // Clear existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (maxWaitTimeoutRef.current) {
      clearTimeout(maxWaitTimeoutRef.current);
    }

    // Set up the regular debounce timeout
    timeoutRef.current = setTimeout(updateValue, delay);

    // Set up maxWait timeout if specified
    if (maxWait !== undefined) {
      const timeSinceLastCall = now - lastCallTimeRef.current;
      const remainingMaxWait = maxWait - timeSinceLastCall;

      if (remainingMaxWait <= 0) {
        // Should invoke immediately due to maxWait
        updateValue();
      } else {
        // Set timeout for remaining maxWait time
        maxWaitTimeoutRef.current = setTimeout(updateValue, remainingMaxWait);
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (maxWaitTimeoutRef.current) {
        clearTimeout(maxWaitTimeoutRef.current);
      }
    };
  }, [value, delay, maxWait]);

  return debouncedValue;
};

export default useDebouncedValue;

export const useDebouncedCallback = <T extends (...args: unknown[]) => unknown>(
  fn: T,
  timeout: number,
  dependencies: unknown[],
) => {
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  return React.useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        fn(...args);
      }, timeout);
    },
    [fn, timeout, ...dependencies],
  );
};
